import React from 'react';
import styled from 'styled-components';

// --- STYLED COMPONENTS FOR RESULTS VIEW ---

const ResultsContainer = styled.div`
  background: #ffffff;
  margin: 0 1rem 1rem;
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 450px;
  overflow-y: auto;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: #0f172a;
  font-weight: 700;
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.825rem;

  th {
    padding: 10px 8px;
    color: #64748b;
    font-size: 0.725rem;
    text-transform: uppercase;
    border-bottom: 2px solid #e2e8f0;
    position: sticky;
    top: 0;
    background: #ffffff;
    z-index: 1;
  }

  td {
    padding: 10px 8px;
    border-bottom: 1px solid #f1f5f9;
    color: #0f172a;
    font-weight: 500;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const Badge = styled.span`
  background: #f1f5f9;
  color: #334155;
  font-weight: 700;
  font-size: 0.7rem;
  padding: 4px 6px;
  border-radius: 4px;
  text-transform: uppercase;
`;

const PickBadge = styled.span`
  background: ${(props) => (props.$pick === 'OVER' ? '#dcfce7' : '#fee2e2')};
  color: ${(props) => (props.$pick === 'OVER' ? '#15803d' : '#b91c1c')};
  font-weight: 800;
  font-size: 0.75rem;
  padding: 3px 8px;
  border-radius: 6px;
  text-transform: uppercase;
  display: inline-block;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`;

const ActionButton = styled.button`
  padding: 8px 14px;
  font-size: 0.825rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }
`;

const ResetButton = styled(ActionButton)`
  background-color: #475569;
  color: white;
`;

const EmptyText = styled.p`
  color: #64748b;
  font-size: 0.875rem;
  text-align: center;
  margin: 1rem 0;
`;

// --- NEW COMPONENT: LinesResultsView ---

export function LinesResultsView({ data, onReset }) {
  if (!data || data.length === 0) {
    return (
      <ResultsContainer>
        <EmptyText>No prediction data returned.</EmptyText>
        {onReset && (
          <ActionRow>
            <ResetButton onClick={onReset}>Go Back</ResetButton>
          </ActionRow>
        )}
      </ResultsContainer>
    );
  }

  return (
    <ResultsContainer>
      <HeaderRow>
        <Title>Model Predictions ({data.length})</Title>
      </HeaderRow>

      <ResultsTable>
        <thead>
          <tr>
            <th>Player</th>
            <th>Metric</th>
            <th>Model</th>
            <th>Pred</th>
            <th>Line</th>
            <th>Edge</th>
            <th>Pick</th>
            <th>Win %</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => {
            const [
              player,
              metric,
              model,
              prediction,
              line,
              edge,
              rmse,
              pick,
              winProb,
            ] = row;

            return (
              <tr key={`${player}-${model}-${idx}`}>
                <td style={{ fontWeight: 600 }}>{player}</td>
                <td>
                  <Badge>{metric}</Badge>
                </td>
                <td style={{ textTransform: 'capitalize', color: '#475569' }}>
                  {model}
                </td>
                <td>{Number(prediction).toFixed(2)}</td>
                <td style={{ fontWeight: 700 }}>{line}</td>
                <td
                  style={{
                    color: edge >= 0 ? '#16a34a' : '#dc2626',
                    fontWeight: 600,
                  }}
                >
                  {edge >= 0 ? `+${edge}` : edge}
                </td>
                <td>
                  <PickBadge $pick={pick}>{pick}</PickBadge>
                </td>
                <td style={{ fontWeight: 700 }}>
                  {(winProb * 100).toFixed(1)}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </ResultsTable>

      <ActionRow>
        {onReset && <ResetButton onClick={onReset}>Back to Lines</ResetButton>}
      </ActionRow>
    </ResultsContainer>
  );
}

export default LinesResultsView;